import { IBaseCompany } from 'FeatureModules/Auth/types/company.types';
import { ICompanyUser } from 'FeatureModules/Auth/types/companyUser.types';
import { IRawMaterial } from 'FeatureModules/ComponentChain/types/component.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { IGraphQlConnection } from 'types/core.types';
import { TBasicLogAction } from 'types/log.types';

export enum EDatasetColumn {
  FARM_ID = 'FARM_ID',
  FARM_NAME = 'FARM_NAME',
  PLOT_ID = 'PLOT_ID',
  PLOT_NAME = 'PLOT_NAME',
  PLOT_SIZE_KM2 = 'PLOT_SIZE_KM2',
  PLOT_SIZE_HA = 'PLOT_SIZE_HA',
  ORIGIN_CLUSTER_ID = 'ORIGIN_CLUSTER_ID',
  FARM_OWNER_FULL_NAME = 'FARM_OWNER_FULL_NAME',
  FARM_OWNER_FIRST_NAME = 'FARM_OWNER_FIRST_NAME',
  FARM_OWNER_LAST_NAME = 'FARM_OWNER_LAST_NAME',
  NUMBER_OF_FARMERS = 'NUMBER_OF_FARMERS',
  GEOMETRY_WKT = 'GEOMETRY_WKT',
  COORDINATES_LAT_LNG = 'COORDINATES_LAT_LNG',
  COORDINATES_LNG_LAT = 'COORDINATES_LNG_LAT',
  LATITUDE = 'LATITUDE',
  LONGITUDE = 'LONGITUDE',
  FARM_SIZE_KM2 = 'FARM_SIZE_KM2',
  FARM_SIZE_HA = 'FARM_SIZE_HA',
  ADDITIONAL_CROPS = 'ADDITIONAL_CROPS',
  LAND_OWNERSHIP = 'LAND_OWNERSHIP',
  SITE_DESCRIPTION = 'SITE_DESCRIPTION',
}

export type TPlotGeometryDatasetColumn = `${EDatasetColumn.GEOMETRY_WKT}`;

export type TPlotSizeDatasetColumn =
  | `${EDatasetColumn.PLOT_SIZE_KM2}`
  | `${EDatasetColumn.PLOT_SIZE_HA}`;

export type TFarmSizeDatasetColumn =
  | `${EDatasetColumn.FARM_SIZE_KM2}`
  | `${EDatasetColumn.FARM_SIZE_HA}`;

export enum EDatasetErrorEntity {
  SITE = 'SITE',
  DOCUMENT = 'DOCUMENT',
  DATASET = 'DATASET',
}

export type TDatasetColumnType = `${EDatasetColumn}`;

export type TDatasetErrorEntity = `${EDatasetErrorEntity}`;

export interface IDatasetCellIssue {
  entityId: string;
  entityType: TDatasetErrorEntity;
  issueMessage: string;
}

export interface IDatasetLogs {
  id: string;
  comment: string;
  userEmail: string;
  user: ICompanyUser;
  company: IBaseCompany;
  timestamp: Date;
  action: TBasicLogAction;
}

export interface IDataset {
  id: string;
  title: string;
  createdTimestamp: Date;
  modifiedTimestamp: Date;
  originCountry: string;
  documents: { id: string; title: string }[];

  /**
   * PartnerCompany base values
   */
  ownedBy: IBasePartnerCompany;
  rawMaterial: IRawMaterial;
  logs?: IDatasetLogs[];
  issuesCount: number;
  issues?: IGeoDataIssue[];
  status: EGeoDataStatus;
  originalGeoDataId: string;
  isDeletable: boolean;

  /**
   * Should only be used in their respective state MAPPED / MAPPING_FAILED and VALIDATED / VALIDATION_FAILED
   */
  tableRepresentation?: TDatasetTableRepresentation;
  mappedColumns?: IColumnsData[];
}

export enum EDatasetView {
  FARMS = 'FARMS',
  PLOTS = 'PLOTS',
  MAP = 'MAP',
}

export interface IDatasetsResponse {
  datasets: IGraphQlConnection<IDataset>;
}

export interface IColumnsData {
  columnName: string;
  columnDataExample: string[]; // Array of strings representing first 5 items as examples
  mappedColumnName?: TDatasetColumnType; // The backend tries to automatically map columns based on the column name
}

export enum EDatasetProcessingErrorType {
  FILE_READ_ERROR = 'FILE_READ_ERROR',
  UNSUPPORTED_FILE_TYPE = 'UNSUPPORTED_FILE_TYPE',
  UNSUPPORTED_GEOMETRY_TYPE = 'UNSUPPORTED_GEOMETRY_TYPE',
  PARSING_ERROR = 'PARSING_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  MAPPING_ERROR = 'MAPPING_ERROR',
  NORMALIZATION_ERROR = 'NORMALIZATION_ERROR',
  OPEN_GEOJSON_GEOMETRY = 'OPEN_GEOJSON_GEOMETRY',
}

export type TDatasetProcessingErrorType = `${EDatasetProcessingErrorType}`;

export enum EDatasetIssueSeverityLevel {
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

export type TDatasetIssueSeverityLevel = `${EDatasetIssueSeverityLevel}`;

export enum EIDatasetCellIssueCode {
  E1 = 'I1', // Geometry WKT is missing
  E2 = 'I2', // Invalid WKT format
  E3 = 'I3', // Geometry is not within valid bounds
  E4 = 'I4', // Unsupported geometry type
  E5 = 'I5', // Farm size must be a float or integer
  E6 = 'I6', // Farm belongs to multiple site clusters
  E7 = 'I7', // Invalid additional crops
  E8 = 'I8', // Invalid land ownership
  E9 = 'I9', // Number of farmers must be an integer
  E10 = 'I10', // Plot size must be a float or integer
  E11 = 'I11', // Duplicate plot ID within the same farm
  E12 = 'I12', // Plot ID is required
  E13 = 'I13', // Origin cluster ID missing
  E14 = 'I14', // Invalid WKT: Geometry must form a closed shape
  E15 = 'I15', // Farm ID is not given
  E16 = 'I16', // Plot ID is already used in imported farms in the platform
}

export type TIDatasetCellIssueCode = `${EIDatasetCellIssueCode}`;

export interface IDatasetCellIssue {
  severityLevel: TDatasetIssueSeverityLevel;
  issueMessage: string;
  issueCode?: TIDatasetCellIssueCode;
}

export type DatasetCell = {
  value?: string | number | Date;
  issues?: IDatasetCellIssue[];
  isValid?: boolean;
};

export type DatasetRow = {
  farmId: DatasetCell;
  plotId: DatasetCell;
  farmName?: DatasetCell;
  plotSizeHa?: DatasetCell;
  plotName?: DatasetCell;
  geometryWkt: DatasetCell;
  farmSizeHa?: DatasetCell;
  originClusterId?: DatasetCell;
  numberOfFarmers?: DatasetCell;
  additionalCrops?: DatasetCell;
  landOwnership?: DatasetCell;
  farmOwnerFullName?: DatasetCell;
  farmOwnerFirstName?: DatasetCell;
  farmOwnerLastName?: DatasetCell;
  siteDescription?: DatasetCell;
};

export type TDatasetTableRepresentation = {
  columnsNames: TDatasetColumnType[];
  rows: DatasetRow[];
};

export interface IDatasetError {
  entityId: string;
  entityType: TDatasetErrorEntity;
  errorType?: TDatasetProcessingErrorType;
  errorMessage: string;
}

export interface IDatasetProcessingError {
  datasetId: string;
  datasetErrors: IDatasetError[];
}

export interface IFileConfiguration {
  csvSeparator?: TCsvSeparator;
  decimalSeparator?: TDecimalSeparator;
  autoCloseGeojsonGeometries?: boolean;
}

export enum ECsvSeparator {
  'COMMA' = 'COMMA',
  'SEMICOLON' = 'SEMICOLON',
  'TAB' = 'TAB',
  'AUTO_DETECT' = 'AUTO_DETECT',
}

export enum EDecimalSeparator {
  'DOT' = 'DOT',
  'COMMA' = 'COMMA',
}

export type TCsvSeparator = `${ECsvSeparator}`;

export type TDecimalSeparator = `${EDecimalSeparator}`;

export enum EGeoDataStatus {
  CREATED = 'CREATED',
  PARSED = 'PARSED',
  PARSING_FAILED = 'PARSING_FAILED',
  MAPPED = 'MAPPED',
  MAPPING_FAILED = 'MAPPING_FAILED',
  VALIDATED = 'VALIDATED',
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  NORMALISED = 'NORMALISED',
  NORMALISATION_FAILED = 'NORMALISATION_FAILED',
  VERIFICATION_FAILED_WITH_ERRORS = 'VERIFICATION_FAILED_WITH_ERRORS',
  VERIFICATION_FAILED_WITH_WARNINGS = 'VERIFICATION_FAILED_WITH_WARNINGS',
  COMPLETED = 'COMPLETED',
}

export type TGeoDataStatus = `${EGeoDataStatus}`;

export enum EGeoDataIssueSeverityLevel {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
}

export type TGeoDataIssueSeverityLevel = `${EGeoDataIssueSeverityLevel}`;

export enum EGeoDataIssueCode {
  GEOM_OUTSIDE_LAND = 'GEOM_OUTSIDE_LAND',
  GEOM_OUTSIDE_COUNTRY = 'GEOM_OUTSIDE_COUNTRY',
  GEOM_SINGLE_POINT_TOO_LARGE = 'GEOM_SINGLE_POINT_TOO_LARGE',
  GEOM_OVERLAP_OTHER_PLOT = 'GEOM_OVERLAP_OTHER_PLOT',
  GEOM_SELF_INTERSECTION = 'GEOM_SELF_INTERSECTION',
  GEOM_PLOTS_TOO_DISTANT = 'GEOM_PLOTS_TOO_DISTANT',
  GEOM_SLIVER_PLOT = 'GEOM_SLIVER_PLOT',
}

export type TGeoDataIssueCode = `${EGeoDataIssueCode}`;

export enum IGeoDataErrorEntity {
  SITE = 'SITE',
  PLOT = 'PLOT',
  GEO_DATA = 'GEO_DATA',
}

export type TGeoDataErrorEntity = `${IGeoDataErrorEntity}`;
export interface IGeoDataIssue {
  geoDataId: string;
  siteId?: string;
  entityId?: string;
  entityType: TGeoDataErrorEntity;
  message: string;
  code: TGeoDataIssueCode;
  severityLevel: TGeoDataIssueSeverityLevel;
}
